import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { 
  getNewsletterSubscribers, 
  deleteNewsletterSubscriber, 
  updateNewsletterSubscriber,
  sendSubscriberEmail,
  sendBulkSubscriberEmail,
  getEmailLogs
} from '../../services/newsletterService';
import Pagination from '../../components/Pagination/Pagination';
import './AdminSubscribersPage.css';

const AdminSubscribersPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [countryFilter, setCountryFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 7,
    total: 0,
    totalPages: 0
  });

  // Email modal state
  const [emailModal, setEmailModal] = useState({ open: false, to: '', toName: '', isBulk: false, recipients: [] });
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // { type: 'success'|'error', msg }

  // Email history state
  const [historyModal, setHistoryModal] = useState({ open: false, email: '', name: '' });
  const [emailLogs, setEmailLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Fetch subscribers
  const fetchSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      if (countryFilter) {
        params.country = countryFilter;
      }
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const data = await getNewsletterSubscribers(params);
      setSubscribers(data.subscribers || []);
      setPagination(prev => ({
        ...prev,
        ...data.pagination
      }));
    } catch (err) {
      setError('Failed to fetch subscribers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, countryFilter, searchQuery]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedSubscribers([]);
    } else {
      setSelectedSubscribers(subscribers.map(s => s.id));
    }
    setSelectAll(!selectAll);
  };

  // Handle individual select
  const handleSelectSubscriber = (id) => {
    if (selectedSubscribers.includes(id)) {
      setSelectedSubscribers(selectedSubscribers.filter(s => s !== id));
    } else {
      setSelectedSubscribers([...selectedSubscribers, id]);
    }
  };

  // Handle send email - open internal modal
  const handleSendEmail = (email, name) => {
    setEmailModal({ open: true, to: email, toName: name || '', isBulk: false, recipients: [] });
    setEmailForm({ subject: '', message: '' });
    setEmailStatus(null);
  };

  // Handle bulk email
  const handleBulkEmail = () => {
    if (selectedSubscribers.length === 0) return;
    const selected = subscribers.filter(s => selectedSubscribers.includes(s.id));
    setEmailModal({
      open: true,
      to: '',
      toName: '',
      isBulk: true,
      recipients: selected.map(s => ({ email: s.email, name: s.name || '' }))
    });
    setEmailForm({ subject: '', message: '' });
    setEmailStatus(null);
  };

  // Send email via backend
  const handleEmailSubmit = async () => {
    if (!emailForm.subject.trim() || !emailForm.message.trim()) return;
    setSendingEmail(true);
    setEmailStatus(null);
    try {
      if (emailModal.isBulk) {
        const result = await sendBulkSubscriberEmail({
          recipients: emailModal.recipients,
          subject: emailForm.subject.trim(),
          message: emailForm.message.trim()
        });
        setEmailStatus({
          type: result.results?.failed > 0 ? 'warning' : 'success',
          msg: `Sent: ${result.results?.sent || 0}, Failed: ${result.results?.failed || 0}`
        });
      } else {
        await sendSubscriberEmail({
          recipientEmail: emailModal.to,
          recipientName: emailModal.toName,
          subject: emailForm.subject.trim(),
          message: emailForm.message.trim()
        });
        setEmailStatus({ type: 'success', msg: 'Email sent successfully' });
      }
      // Auto-close after 2s on success
      setTimeout(() => setEmailModal(m => m.open ? { ...m, open: false } : m), 2000);
    } catch (err) {
      setEmailStatus({ type: 'error', msg: err.error || err.message || 'Failed to send email' });
    } finally {
      setSendingEmail(false);
    }
  };

  // View email history for a subscriber
  const handleViewHistory = async (email, name) => {
    setHistoryModal({ open: true, email, name: name || email });
    setLogsLoading(true);
    try {
      const data = await getEmailLogs({ recipientEmail: email, limit: 50 });
      setEmailLogs(data.logs || []);
    } catch (err) {
      setEmailLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  // Generate page numbers
  const getPageNumbers = () => {
    const pages = [];
    const maxPages = Math.min(5, pagination.totalPages);
    let startPage = Math.max(1, pagination.page - 2);
    let endPage = Math.min(pagination.totalPages, startPage + maxPages - 1);
    
    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <AdminLayout>
      <div className="admin-subscribers-page">
        {/* Page Header */}
        <div className="subscribers-page-header">
          <h1 className="subscribers-page-title">Subscriber</h1>
        </div>

        {/* Subscribers List Section */}
        <div className="subscribers-list-section">
          {/* List Header with Title, Search and Filters */}
          <div className="subscribers-list-header">
            <h2 className="subscribers-list-title">Subscriber Lists</h2>
            
            {/* Bulk Email Button */}
            {selectedSubscribers.length > 0 && (
              <button className="subscribers-bulk-email-btn" onClick={handleBulkEmail}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Send Bulk Email ({selectedSubscribers.length})
              </button>
            )}

            {/* Search Box */}
            <div className="subscribers-search-box">
              <input 
                type="text" 
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.166 14.168L17.4993 17.5013" stroke="#969696" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667Z" stroke="#969696" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            {/* Country Filter */}
            <div className="subscribers-country-filter">
              <select 
                value={countryFilter}
                onChange={(e) => {
                  setCountryFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="">Country</option>
                <option value="UAE">UAE</option>
                <option value="Dubai">Dubai</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="Qatar">Qatar</option>
                <option value="Kuwait">Kuwait</option>
                <option value="Bahrain">Bahrain</option>
                <option value="Oman">Oman</option>
              </select>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          {error && <div className="subscribers-error-message">{error}</div>}

          {loading ? (
            <div className="subscribers-loading">Loading subscribers...</div>
          ) : subscribers.length === 0 ? (
            <div className="subscribers-empty-state">
              <p>No subscribers found.</p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="subscribers-table">
                {/* Table Header */}
                <div className="subscribers-table-header">
                  <div className="subscribers-th-checkbox">
                    <input 
                      type="checkbox" 
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                    <span>All</span>
                  </div>
                  <div className="subscribers-th">Name</div>
                  <div className="subscribers-th">Location</div>
                  <div className="subscribers-th">Gmail</div>
                  <div className="subscribers-th">Action</div>
                </div>

                {/* Table Body */}
                <div className="subscribers-table-body">
                  {subscribers.map((subscriber) => (
                    <div key={subscriber.id} className="subscribers-table-row">
                      <div className="subscribers-td-checkbox">
                        <input 
                          type="checkbox"
                          checked={selectedSubscribers.includes(subscriber.id)}
                          onChange={() => handleSelectSubscriber(subscriber.id)}
                        />
                      </div>
                      <div className="subscribers-td subscribers-td-name">
                        {subscriber.name || 'Eiffage Construction'}
                      </div>
                      <div className="subscribers-td">
                        {subscriber.location || subscriber.country || 'Dubai'}
                      </div>
                      <div className="subscribers-td">
                        {subscriber.email || 'company@gmail.com'}
                      </div>
                      <div className="subscribers-td-action">
                        <span 
                          className="send-email-link"
                          onClick={() => handleSendEmail(subscriber.email, subscriber.name)}
                        >
                          Send Email
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3.33203 8H12.6654" stroke="#6D6D6D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M8 3.33203L12.6667 7.9987L8 12.6654" stroke="#6D6D6D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        <span 
                          className="view-history-link"
                          onClick={() => handleViewHistory(subscriber.email, subscriber.name)}
                        >
                          View Emails
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pagination */}
              <Pagination currentPage={pagination.page} totalPages={pagination.totalPages} onPageChange={(p) => setPagination(prev => ({ ...prev, page: p }))} />
            </>
          )}
        </div>
      </div>

      {/* ===== SEND EMAIL MODAL ===== */}
      {emailModal.open && ReactDOM.createPortal(
        <div className="sub-email-overlay" onClick={() => !sendingEmail && setEmailModal({ ...emailModal, open: false })}>
          <div className="sub-email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-email-modal-header">
              <h2>{emailModal.isBulk ? `Send Bulk Email (${emailModal.recipients.length} recipients)` : 'Send Email'}</h2>
              <button className="sub-email-close" onClick={() => !sendingEmail && setEmailModal({ ...emailModal, open: false })}>&times;</button>
            </div>
            <div className="sub-email-modal-body">
              {/* To field */}
              <div className="sub-email-field">
                <label>To</label>
                {emailModal.isBulk ? (
                  <div className="sub-email-bulk-tags">
                    {emailModal.recipients.map((r, i) => (
                      <span key={i} className="sub-email-tag">{r.name || r.email}</span>
                    ))}
                  </div>
                ) : (
                  <input type="text" value={emailModal.to} readOnly className="sub-email-input sub-email-readonly" />
                )}
              </div>
              {/* Subject */}
              <div className="sub-email-field">
                <label>Subject</label>
                <input
                  type="text"
                  placeholder="Enter email subject"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  className="sub-email-input"
                  disabled={sendingEmail}
                />
              </div>
              {/* Message */}
              <div className="sub-email-field">
                <label>Message</label>
                <textarea
                  placeholder="Write your message here..."
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                  className="sub-email-textarea"
                  rows="6"
                  disabled={sendingEmail}
                />
              </div>
              {/* Status message */}
              {emailStatus && (
                <div className={`sub-email-status sub-email-status-${emailStatus.type}`}>
                  {emailStatus.type === 'success' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2"/><polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" strokeWidth="2"/></svg>}
                  {emailStatus.type === 'error' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/><line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/></svg>}
                  {emailStatus.msg}
                </div>
              )}
            </div>
            <div className="sub-email-modal-footer">
              <button className="sub-email-cancel" onClick={() => !sendingEmail && setEmailModal({ ...emailModal, open: false })} disabled={sendingEmail}>Cancel</button>
              <button
                className="sub-email-send"
                onClick={handleEmailSubmit}
                disabled={sendingEmail || !emailForm.subject.trim() || !emailForm.message.trim()}
              >
                {sendingEmail ? (
                  <><span className="sub-email-spinner"></span> Sending...</>
                ) : (
                  <><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg> Send Email</>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ===== EMAIL HISTORY MODAL ===== */}
      {historyModal.open && ReactDOM.createPortal(
        <div className="sub-email-overlay" onClick={() => setHistoryModal({ ...historyModal, open: false })}>
          <div className="sub-email-modal sub-history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-email-modal-header">
              <h2>Email History — {historyModal.name}</h2>
              <button className="sub-email-close" onClick={() => setHistoryModal({ ...historyModal, open: false })}>&times;</button>
            </div>
            <div className="sub-email-modal-body">
              {logsLoading ? (
                <div className="sub-history-loading">Loading email history...</div>
              ) : emailLogs.length === 0 ? (
                <div className="sub-history-empty">No emails sent to this subscriber yet.</div>
              ) : (
                <div className="sub-history-list">
                  {emailLogs.map((log) => (
                    <div key={log.id} className="sub-history-item">
                      <div className="sub-history-row">
                        <span className="sub-history-subject">{log.subject}</span>
                        <span className={`sub-history-badge sub-history-${log.status}`}>{log.status}</span>
                      </div>
                      <div className="sub-history-message">{log.message.length > 150 ? log.message.substring(0, 150) + '...' : log.message}</div>
                      <div className="sub-history-meta">
                        <span>{new Date(log.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                        {log.sentByName && <span>by {log.sentByName}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </AdminLayout>
  );
};

export default AdminSubscribersPage;
